import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Rol } from '@lexicore/shared';
import { almacenSesion, api, estaTokenExpirado, registrarCierreDeSesion, renovarSesion } from '../lib/api';

export interface UsuarioSesion {
  id: string;
  nombre: string;
  email: string;
  rol: Rol;
  tenantId: string;
  tenantNombre: string;
}

interface RespuestaSesion {
  accessToken: string;
  refreshToken: string;
  usuario: UsuarioSesion;
}

interface ContextoAuth {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  iniciarSesion: (email: string, password: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  /** `true` si el rol actual alcanza el mínimo pedido. */
  puede: (rolMinimo: Rol) => boolean;
}

const Contexto = createContext<ContextoAuth | null>(null);

/** Mismo orden jerárquico que el backend: menor índice, más privilegios. */
const JERARQUIA: Rol[] = ['OWNER', 'ADMIN', 'GESTOR', 'FINANZAS', 'LECTOR'];

export function ProveedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);

  /**
   * Al arrancar se comprueba si el token guardado sigue sirviendo.
   *
   * Se pregunta a la API en vez de confiar en el token: puede haber sido
   * revocado desde otro dispositivo, y mostrar un CRM que después falla en cada
   * petición es peor que mandar al login de entrada.
   */
  useEffect(() => {
    let vigente = true;

    (async () => {
      const acceso = almacenSesion.acceso();
      if (!acceso) {
        setCargando(false);
        return;
      }

      // Si el token de acceso ya expiró, intentar renovarlo antes de llamar a /auth/yo
      if (estaTokenExpirado(acceso)) {
        const renovada = await renovarSesion();
        if (!renovada || !vigente) {
          if (vigente) {
            almacenSesion.limpiar();
            setCargando(false);
          }
          return;
        }
      }

      try {
        const datos = await api<UsuarioSesion & { usuarioId?: string }>('/auth/yo');
        if (!vigente) return;

        // `/auth/yo` devuelve el contexto del token; se completa con lo que ya
        // se sabía de la sesión anterior para no perder el nombre de la empresa.
        setUsuario({
          id: datos.usuarioId ?? datos.id,
          nombre: datos.nombre ?? datos.email,
          email: datos.email,
          rol: datos.rol,
          tenantId: datos.tenantId,
          tenantNombre: datos.tenantNombre ?? '',
        });
      } catch {
        if (vigente) almacenSesion.limpiar();
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  // El cliente HTTP avisa cuando la renovación falla definitivamente.
  useEffect(() => {
    registrarCierreDeSesion(() => setUsuario(null));
  }, []);

  const iniciarSesion = useCallback(async (email: string, password: string) => {
    const sesion = await api<RespuestaSesion>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    almacenSesion.guardar(sesion.accessToken, sesion.refreshToken);
    setUsuario(sesion.usuario);
  }, []);

  const cerrarSesion = useCallback(async () => {
    const refresco = almacenSesion.refresco();

    // Se revoca en el servidor, pero la sesión local se limpia pase lo que pase:
    // si el usuario pidió salir, tiene que salir aunque la red falle.
    if (refresco) {
      await api('/auth/cerrar-sesion', { method: 'POST', body: { refreshToken: refresco } }).catch(
        () => undefined,
      );
    }

    almacenSesion.limpiar();
    setUsuario(null);
  }, []);

  const puede = useCallback(
    (rolMinimo: Rol) => {
      if (!usuario) return false;
      return JERARQUIA.indexOf(usuario.rol) <= JERARQUIA.indexOf(rolMinimo);
    },
    [usuario],
  );

  const valor = useMemo(
    () => ({ usuario, cargando, iniciarSesion, cerrarSesion, puede }),
    [usuario, cargando, iniciarSesion, cerrarSesion, puede],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): ContextoAuth {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAuth debe usarse dentro de <ProveedorAuth>.');
  return contexto;
}
